import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { findLikelyDuplicateMembers } from './findDuplicateMembers';

const { FiAlertTriangle, FiCheckCircle, FiArrowRight, FiUsers } = FiIcons;

function MemberMatch({ member, onReview }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl bg-white/80 p-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-accent-navy">{member.name}</p>
        <p className="mt-0.5 text-[10px] text-text-muted">
          {member.relation ?? 'Family member'}
          {member.birthYear ? ` · Born ${member.birthYear}` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onReview(member)}
        className="shrink-0 rounded-full border border-brand-dark px-3 py-1.5 text-[9px] font-black uppercase tracking-wider text-accent-navy transition hover:border-accent-gold"
      >
        Review
      </button>
    </div>
  );
}

const FamilyTreeDuplicateDetection = ({ nodes, onReview }) => {
  const matches = useMemo(() => findLikelyDuplicateMembers(nodes), [nodes]);

  return (
    <section className="rounded-[28px] border border-brand-dark bg-white p-5 shadow-sm md:p-6">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          matches.length ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
        }`}>
          <SafeIcon icon={matches.length ? FiAlertTriangle : FiCheckCircle} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-serif text-lg text-accent-navy">Duplicate detection</h4>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            Possible matches are found by comparing member names and birth years. Review suggestions before making any changes.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-light px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-accent-navy">
          <SafeIcon icon={FiUsers} className="h-3 w-3" />
          {matches.length} {matches.length === 1 ? 'match' : 'matches'}
        </span>
      </div>

      {matches.length ? (
        <div className="mt-4 space-y-3">
          {matches.map(({ first, second, score, reason }) => (
            <div key={`${first.id}-${second.id}`} className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-[9px] font-black uppercase tracking-widest text-amber-800">{reason}</span>
                <span className="text-[9px] font-bold text-amber-800">{score}% confidence</span>
              </div>
              <div className="grid gap-2 md:grid-cols-[1fr_auto_1fr] md:items-center">
                <MemberMatch member={first} onReview={onReview} />
                <SafeIcon icon={FiArrowRight} className="hidden h-4 w-4 text-amber-700 md:block" />
                <MemberMatch member={second} onReview={onReview} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl bg-green-50/70 px-4 py-3 text-xs text-green-800">
          No likely duplicate members found in this family tree.
        </div>
      )}
    </section>
  );
};

export default FamilyTreeDuplicateDetection;